import { GraphQLFieldConfig, GraphQLNonNull } from 'graphql'
import {
  GenericMutationResponseType,
  GenericMutationResponse
} from '../types/generic-mutation-response'
import { Context } from '../shared'
import {
  ServerDeleteMutationArgs,
  serverDeleteMutationArgs,
  ServersDeleteMutationArgs,
  serversDeleteMutationArgs
} from '../types/server-delete-mutation-args'
import {
  ServerFilterDeleteMutationArgs,
  serverFilterDeleteMutationArgs
} from '../types/server-filter-delete-mutation-args'
import { mapServerFilters } from '../endpoint-helpers/servers'
import { Connector } from '../../../api/connector'

async function deleteServerRequest(
  connector: Connector,
  organizationID: string,
  id: string,
  deleteVulnerabilities: boolean
) {
  return connector.executeDelete(
    `/${organizationID}/servers/${id}?deletetraces=${deleteVulnerabilities}`
  )
}

export const deleteServer: GraphQLFieldConfig<
  any,
  Context,
  ServerDeleteMutationArgs
> = {
  description: 'Delete a server',
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: serverDeleteMutationArgs,
  resolve: async (
    _source,
    args,
    context: Context
  ): Promise<GenericMutationResponse> => {
    const { input } = args

    if (!input) {
      throw new Error('mutation input parameter is required')
    }

    const { id, organizationID, deleteVulnerabilities } = input

    const { connector } = context

    await deleteServerRequest(
      connector,
      organizationID,
      id,
      deleteVulnerabilities
    )

    return { _placeholder: true }
  }
}

export const deleteServers: GraphQLFieldConfig<
  any,
  Context,
  ServersDeleteMutationArgs
> = {
  description: 'Delete servers',
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: serversDeleteMutationArgs,
  resolve: async function deleteServers(
    _source,
    args,
    context
  ): Promise<GenericMutationResponse> {
    const { input } = args

    if (!input) {
      throw new Error('mutation input parameter is required')
    }

    const { ids, organizationID, deleteVulnerabilities } = input

    const { connector } = context
    await Promise.all(
      ids.map((serverID) =>
        deleteServerRequest(
          connector,
          organizationID,
          serverID,
          deleteVulnerabilities
        )
      )
    )

    return { _placeholder: true }
  }
}

export const deleteServersByFilters: GraphQLFieldConfig<
  any,
  Context,
  ServerFilterDeleteMutationArgs
> = {
  description: 'Delete servers by filters',
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: serverFilterDeleteMutationArgs,
  resolve: async function deleteServersByFilters(
    _source,
    args,
    context
  ): Promise<GenericMutationResponse> {
    const { input } = args

    if (!input) {
      throw new Error('mutation input parameter is required')
    }

    const { filters, organizationID, deleteVulnerabilities } = input

    const { connector } = context
    await connector.executeDelete(`/${organizationID}/servers/bulk`, {
      filters: mapServerFilters(filters),
      deleteVulnerabilities
    })

    return { _placeholder: true }
  }
}
